import { useCallback, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { loadDirectMetricsAsync } from '../../../../data-layer/direct/actions';
import { selectMetrics } from '../../../../data-layer/direct/selectors';
import { DirectCampaignUI } from '../../../../data-layer/direct/types';
import { useDispatch } from '../../../../data-layer/useDispatch';
import { useSelector } from '../../../../data-layer/useSelector';

function getMetricsSettings(company?: DirectCampaignUI) {
    const { counters, weeklyLimit } = company || {};

    return {
        selectedCounters: counters?.map((counter) => String(counter.counter_id)) ?? [],
        selectedGoals:
            counters?.reduce<Record<string, number | undefined>>((res, counter) => {
                const selected = counter.goals.reduce<Record<string, number | undefined>>(
                    (goals, goal) => ({
                        ...goals,
                        [String(goal.goal_id)]: goal.cost,
                    }),
                    {},
                );

                return {
                    ...res,
                    ...selected,
                };
            }, {}) ?? {},
        weeklyLimit,
    };
}

export const usePromotionMetrics = (company?: DirectCampaignUI) => {
    const dispatch = useDispatch();
    const metrics = useSelector((state) => selectMetrics(state));
    const onLoadMetrics = useCallback(() => dispatch(loadDirectMetricsAsync()), [dispatch]);

    const metricsSettings = getMetricsSettings(company);

    const [selectedCounters, setSelectedCounters] = useState<string[]>(
        metricsSettings.selectedCounters,
    );
    const onSelectCounter = useCallback(
        (key: string) => {
            if (selectedCounters.indexOf(key) !== -1) {
                return;
            }

            setSelectedCounters([...selectedCounters, key]);
        },
        [selectedCounters],
    );
    const onRemoveCounter = useCallback(
        (key: string) => {
            const newSelectedMetrics = selectedCounters.filter((seleced) => seleced !== key);

            setSelectedCounters(newSelectedMetrics);
        },
        [selectedCounters],
    );

    const [selectedGoals, setSelectedGoals] = useState<Record<string, number | undefined>>(
        metricsSettings.selectedGoals,
    );
    const onSelectGoals = useCallback(
        (selected: string[]) => {
            const newSelectedGoals = selected.reduce(
                (res, id) => ({ ...res, [id]: selectedGoals?.[id] }),
                {},
            );
            setSelectedGoals(newSelectedGoals);
        },
        [selectedGoals],
    );
    const onRemoveGoal = useCallback(
        (key: string) => {
            const newSelectedGoals = { ...selectedGoals };
            delete newSelectedGoals[key];

            setSelectedGoals(newSelectedGoals);
        },
        [selectedGoals],
    );
    const onChangeGoalBudget = useCallback(
        (key: string, value: string) => {
            const newSelectedGoals = { ...selectedGoals, [key]: Number(value) };

            setSelectedGoals(newSelectedGoals);
        },
        [selectedGoals],
    );

    const [weeklyLimit, setWeeklyLimit] = useState<number | undefined>(metricsSettings.weeklyLimit);
    const onChangeWeeklyLimit = useCallback((value: string) => {
        setWeeklyLimit(Number(value));
    }, []);

    useEffectOnce(() => {
        onLoadMetrics();
    });

    return {
        metrics,
        selectedCounters,
        onSelectCounter,
        onRemoveCounter,
        selectedGoals,
        onSelectGoals,
        onRemoveGoal,
        onChangeGoalBudget,
        weeklyLimit,
        onChangeWeeklyLimit,
    };
};
